import { IQuantitiesInputDictonary } from '@/dictionaries/components/quantitiesInput/types';
import useClickOutside from '@/hooks/useClickOutside';
import { cn } from '@/lib/utils';
import { useState, type JSX } from 'react';
import { UseFormRegister } from 'react-hook-form';

export type QuantityOption = {
  name: string;
  label: string;
  value: number;
  min?: number;
  max?: number;
};

type AddedQuantities = {
  adults: number;
  children: number;
  infants: number;
};

type Props = {
  id: string;
  disabled?: boolean;
  onChange: (value: Pick<QuantityOption, 'value' | 'name'>) => void;
  options: QuantityOption[];
  label: string;
  register: UseFormRegister<any>;
  addedQuantities: AddedQuantities;
  dict: IQuantitiesInputDictonary;
  guests?: number;
  infantsTotal?: number;
  adultsTotal?: number;
  childrenTotal?: number;
  className?: string;
  footerText?: JSX.Element;
  onlyNumber?: boolean;
  labelClassName?: string;
};

export default function QuantitieInput({
  id,
  disabled = false,
  onChange,
  label,
  options,
  register,
  dict,
  addedQuantities,
  guests,
  infantsTotal,
  adultsTotal,
  childrenTotal,
  className,
  footerText,
  onlyNumber = false,
  labelClassName,
}: Props) {
  const [open, setOpen] = useState(false);
  const { adults, children, infants } = addedQuantities;
  const total = adults + children;
  const showPlusSign = infants > 0 && (adults > 0 || children > 0);

  const ref = useClickOutside({ clickFn: () => setOpen(false) });

  function getDynamicLabel() {
    let label: string = '';
    if (onlyNumber) {
      label = `${total + infants}`;
    } else {
      if (adults > 0 || children > 0) {
        label = `${adults + children} ${dict.guest}`;
      }
      if (adults > 1 || children > 1) {
        label = `${adults + children} ${dict.guests}`;
      }
    }
    return label;
  }

  function getDynamicInfantLabel() {
    let label: string = '';
    if (infants > 0 && !onlyNumber) {
      label = `${infants} ${dict.infant}`;
    }
    if (infants > 1 && !onlyNumber) {
      label = `${infants} ${dict.infants}`;
    }
    return label;
  }

  function defineMaxQuantity(name: string, nameIndex: keyof AddedQuantities) {
    if (name == 'adults' && adultsTotal != undefined) {
      return addedQuantities[nameIndex] === adultsTotal;
    }
    if (name == 'children' && childrenTotal != undefined) {
      return addedQuantities[nameIndex] === childrenTotal;
    }
    if (name == 'infants' && infantsTotal !== undefined) {
      return addedQuantities[nameIndex] === infantsTotal;
    }
    return total === guests;
  }

  return (
    <div ref={ref} className={className}>
      <div className="relative">
        <label
          className={cn(
            'text-sm text-gray-700 leading-[1.8] w-fit',
            labelClassName,
          )}
          htmlFor={id}>
          {label}
        </label>
        <button
          id={id}
          type="button"
          className="relative w-full min-h-[44px] h-[2.5625rem] leading-[1.625] border bg-gray-100 border-gray-200 pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none text-base"
          aria-haspopup="listbox"
          aria-expanded="true"
          aria-labelledby="listbox-label"
          onClick={() => setOpen(!open)}
          disabled={disabled}>
          <span
            className={`absolute inset-y-0 right-0 w-full flex items-center pointer-events-none ${
              disabled ? 'hidden' : ''
            }`}>
            <p className="pl-4">
              {adults + children + infants > 0
                ? `${getDynamicLabel()}${
                    infants > 0 && !onlyNumber
                      ? `${showPlusSign ? ' +' : ''} ${getDynamicInfantLabel()}`
                      : ''
                  }`
                : dict.placeholder}
            </p>
            <svg
              className="h-8 w-8 text-gray-500 ml-auto"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 -960 960 960"
              fill="currentColor">
              <path d="M480-357.85 253.85-584 296-626.15l184 184 184-184L706.15-584 480-357.85Z" />
            </svg>
          </span>
        </button>

        <div
          aria-label="listbox tooltip pointer"
          className={cn(
            'absolute desktop:left-[30%] right-[13%] mt-[12px] z-20 rotate-45 w-0 h-0 backdrop-blur-none',
            {
              'w-[20px] h-[20px] block border-t border-l bg-gray-100 border-blue':
                open,
            },
          )}
        />
        <ul
          className={cn(
            'max-h-0 absolute right-0 desktop:left-0 mt-[22px] z-10 w-full  rounded-[5px] overflow-hidden text-base focus:outline-none sm:text-sm transition-[max-height] duration-[200ms] ease-in-out',
            {
              'max-h-[200px] w-[284px] py-[15px] px-[22px] border border-blue bg-gray-100':
                open,
              'max-h-[190x] left-0 [@media(min-width:1900px)]:left-auto':
                guests,
            },
          )}>
          {open && (
            <div className="h-full flex flex-col">
              {options.map(({ label, name }, index) => {
                const nameIndex = name as keyof AddedQuantities;
                const disabled = addedQuantities[nameIndex] === 0;
                return (
                  <li
                    key={`${id}-${index}`}
                    className="select-none relative flex items-center">
                    <div className="w-[58%]">
                      <span className="text-sm text-gray-700">{label}</span>
                    </div>
                    <div className="w-[33%] h-10 ml-auto flex items-center justify-between">
                      <button
                        type="button"
                        id={`${label}-decrement-quantity`}
                        aria-label={`${label}-decrement-quantity`}
                        disabled={disabled}
                        onClick={() =>
                          onChange({
                            value: addedQuantities[nameIndex] - 1,
                            name,
                          })
                        }
                        className="text-gray-500 rounded-full border border-gray-500 cursor-pointer flex justify-center items-center disabled:cursor-default disabled:opacity-40">
                        <svg
                          className="w-[25px] h-[25px] fill-current"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 -960 960 960">
                          <path d="M200-440v-80h560v80H200Z" />
                        </svg>
                      </button>
                      <input
                        disabled
                        {...register(`${id}.${name}`)}
                        className="text-lg w-8 h-fit text-center bg-transparent"
                      />
                      <button
                        type="button"
                        id={`${label}-increment-quantity`}
                        aria-label={`${label}-increment-quantity`}
                        disabled={defineMaxQuantity(name, nameIndex)}
                        onClick={() => {
                          onChange({
                            value: addedQuantities[nameIndex] + 1,
                            name,
                          });
                        }}
                        className="text-gray-500 rounded-full border border-gray-500 cursor-pointer flex justify-center items-center disabled:cursor-default disabled:opacity-40">
                        <svg
                          className="w-[25px] h-[25px] fill-current"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 -960 960 960">
                          <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                        </svg>
                      </button>
                    </div>
                  </li>
                );
              })}
              <div>{footerText}</div>
            </div>
          )}
          {guests && open && (
            <div className="mt-2">
              <small className="text-sm">
                {dict.youCanAddAMaximumOf}{' '}
                <span className="font-semibold">{`${guests} ${dict.guestsLabel}`}</span>
                .{dict.infantsDontCountInTotal}
              </small>
            </div>
          )}
        </ul>
      </div>
    </div>
  );
}
